// AgendaView.jsx

import React from 'react';

// Hooks
import useAgendaGroups from './hooks/useAgendaGroups';
import useCalendarDateFields from 'views/calendar/hooks/useCalendarDateFields';
import useCalendarEvents from 'views/calendar/hooks/useCalendarEvents';

// CSS
import './AgendaView.css';

// Components
import AgendaViewDay from './day/AgendaViewDay';

/**
 * AgendaView
 * 
 * Renders the agenda view for the calendar, displaying events grouped by day.
 * 
 * @param {Object} view - The calendar view configuration.
 * @param {Object} typeDef - The type definition for the calendar nodes.
 * @param {Array} nodes - The list of calendar nodes to display.
 * @param {string} language - The language code for formatting dates and times.
 * @param {boolean} compact - Whether to render in compact mode (e.g. for dashboard cards).
 *  
 *
 * @returns {ReactNode} - AgendaView.
 */
const AgendaView = React.memo(({ view, typeDef, nodes = [], eventItems, language = 'en', compact = false, onNodeClick, onNodeDoubleClick }) => {

    // ----- HOOKS ----- //

    // Calendar Date Fields

    const { typeKey, dateFields } = useCalendarDateFields(typeDef, { preferredFieldKey: view?.calendarField });

    // Calendar Events
    const resolvedEventItems = useCalendarEvents({ nodes, dateFields, typeKey, eventItems });

    // Agenda Groups
    const groupedEvents = useAgendaGroups(resolvedEventItems);

    // ----- RENDER ----- //

    return (

        <div className="agenda-view-container">

            {/* LIST */}
            {groupedEvents.length !== 0 &&(

                <div className="agenda-view-list">
                    {groupedEvents.map((group) => (
                        <AgendaViewDay
                          key={group.key}
                          group={group}
                          language={language}
                          compact={compact}
                          onNodeClick={onNodeClick}
                          onNodeDoubleClick={onNodeDoubleClick}
                        />
                    ))}
                </div>
            )}

        </div>
    );

});

// Dev Tools
AgendaView.displayName = 'AgendaView';

export default AgendaView;
